import React, { useState, useEffect } from 'react';
import { WeeklyLockingWithTalents, ProgressSummary, weeklyLockInService } from '../services/weeklyLockInService';

interface WeeklyReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  locking: WeeklyLockingWithTalents;
  progressSummary: ProgressSummary[];
}

interface DayOfWeek {
  date: string;
  dayName: string;
  isToday: boolean;
  totalMinutes: number;
  targetMinutes: number;
  completionPercentage: number;
}

const WeeklyReportModal: React.FC<WeeklyReportModalProps> = ({
  isOpen,
  onClose,
  locking,
  progressSummary
}) => {
  const [weekProgress, setWeekProgress] = useState<DayOfWeek[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      generateWeekProgress();
    }
  }, [isOpen, progressSummary]);

  const generateWeekProgress = async () => {
    try {
      setLoading(true);
      
      const startDate = new Date(locking.week_start_date);
      const endDate = new Date(locking.week_end_date);
      const today = new Date();
      
      const dailyTarget = Math.round(locking.total_time_minutes / 7);
      
      const weekDays: DayOfWeek[] = [];
      
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateString = date.toISOString().split('T')[0];
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        
        // Calculate total minutes for this day across all talents
        const dayMinutes = progressSummary.length > 0 
          ? progressSummary.reduce((total, talent) => {
          const dayProgress = talent.daily_progress.find(p => p.date === dateString);
          return total + (dayProgress?.minutes || 0);
            }, 0)
          : 0;
        
        const completionPercentage = dailyTarget > 0 ? (dayMinutes / dailyTarget) * 100 : 0;
        
        weekDays.push({
          date: dateString,
          dayName,
          isToday: dateString === today.toISOString().split('T')[0],
          totalMinutes: dayMinutes,
          targetMinutes: dailyTarget,
          completionPercentage
        });
      }
      
      setWeekProgress(weekDays);
    } catch (err) {
      console.error('Error generating week progress:', err);
    } finally {
      setLoading(false);
    }
  };

  const getOverallStats = () => {
    const totalTarget = locking.total_time_minutes;
    const totalCompleted = progressSummary.length > 0 
      ? progressSummary.reduce((sum, talent) => sum + talent.completed_minutes, 0)
      : 0;
    const overallCompletion = totalTarget > 0 ? (totalCompleted / totalTarget) * 100 : 0;
    
    const daysWithProgress = weekProgress.filter(day => day.totalMinutes > 0).length;
    const averageDailyMinutes = weekProgress.length > 0 
      ? weekProgress.reduce((sum, day) => sum + day.totalMinutes, 0) / weekProgress.length 
      : 0;
    
    const streak = weekProgress.filter(day => day.completionPercentage >= 80).length;
    
    return {
      totalTarget,
      totalCompleted,
      overallCompletion,
      daysWithProgress,
      averageDailyMinutes,
      streak
    };
  };

  const getBestDay = () => {
    if (weekProgress.length === 0) return null;
    return weekProgress.reduce((best, current) => 
      current.totalMinutes > best.totalMinutes ? current : best
    );
  };

  const getInsights = () => {
    const stats = getOverallStats();
    const insights: string[] = [];
    
    if (stats.overallCompletion >= 90) {
      insights.push("🎉 Outstanding week! You exceeded expectations!");
    } else if (stats.overallCompletion >= 80) {
      insights.push("🌟 Great job! You hit your weekly target!");
    } else if (stats.overallCompletion >= 60) {
      insights.push("👍 Good progress! You're on the right track!");
    } else {
      insights.push("💪 Room for improvement - let's aim higher next week!");
    }
    
    if (stats.daysWithProgress >= 6) {
      insights.push("📅 Excellent consistency - you practiced almost every day!");
    } else if (stats.daysWithProgress >= 4) {
      insights.push("📅 Good consistency - try to practice more days next week!");
    }
    
    // Only analyze best performing talent if we have progress data
    if (progressSummary.length > 0) {
    const bestPerformingTalent = progressSummary.reduce((best, current) => 
      current.completion_percentage > best.completion_percentage ? current : best
    );
    
    if (bestPerformingTalent.completion_percentage >= 90) {
      insights.push(`🏆 ${bestPerformingTalent.talent_name} was your star performer!`);
      }
    } else {
      insights.push("📊 No progress data yet - start tracking your activities!");
    }
    
    return insights;
  };

  const stats = getOverallStats();
  const bestDay = getBestDay();
  const insights = getInsights();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Weekly Report
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            Detailed analysis of your weekly lock-in performance
          </p>
        </div>

        <div className="p-6 overflow-y-auto max-h-[80vh]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Overall Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.overallCompletion.toFixed(0)}%
                  </div>
                  <div className="text-sm text-gray-600">Overall Completion</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {stats.totalCompleted}
                  </div>
                  <div className="text-sm text-gray-600">Minutes Completed</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {stats.daysWithProgress}
                  </div>
                  <div className="text-sm text-gray-600">Active Days</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {Math.round(stats.averageDailyMinutes)}
                  </div>
                  <div className="text-sm text-gray-600">Avg Daily Minutes</div>
                </div>
              </div>

              {/* Week Timeline */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Daily Progress Timeline
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {weekProgress.map((day) => (
                    <div key={day.date} className="text-center">
                      <div className="text-xs text-gray-600 mb-2">
                        {day.dayName}
                      </div>
                      <div className={`w-full h-20 rounded-lg border-2 flex flex-col justify-end p-1 ${
                        day.isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}>
                        <div 
                          className={`w-full rounded transition-all duration-300 ${
                            day.completionPercentage >= 80 ? 'bg-green-500' :
                            day.completionPercentage >= 60 ? 'bg-yellow-500' :
                            day.completionPercentage >= 40 ? 'bg-orange-500' :
                            day.completionPercentage > 0 ? 'bg-red-500' : 'bg-gray-200'
                          }`}
                          style={{ 
                            height: `${Math.max(Math.min(day.completionPercentage, 100), 5)}%` 
                          }}
                        />
                      </div>
                      <div className="text-xs text-gray-900 mt-1 font-medium">
                        {day.totalMinutes}m
                      </div>
                      <div className="text-xs text-gray-500">
                        {day.completionPercentage.toFixed(0)}%
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-center space-x-4 text-xs">
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
                    80%+ Complete
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded mr-1"></div>
                    60-79% Complete
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 rounded mr-1"></div>
                    40-59% Complete
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
                    Under 40%
                  </span>
                </div>
              </div>

              {/* Talent Performance */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Talent Performance Breakdown
                </h3>
                <div className="space-y-4">
                  {progressSummary.map((talent) => (
                    <div key={talent.talent_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-medium text-gray-900">{talent.talent_name}</h4>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${
                            talent.completion_percentage >= 80 ? 'text-green-600' :
                            talent.completion_percentage >= 60 ? 'text-yellow-600' :
                            talent.completion_percentage >= 40 ? 'text-orange-600' :
                            'text-red-600'
                          }`}>
                            {talent.completion_percentage.toFixed(0)}%
                          </div>
                          <div className="text-sm text-gray-600">
                            {talent.completed_minutes}/{talent.target_minutes} min
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div 
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${Math.min(talent.completion_percentage, 100)}%` }}
                        />
                      </div>
                      
                      {/* Daily breakdown */}
                      <div className="grid grid-cols-7 gap-1">
                        {talent.daily_progress.map((day, index) => (
                          <div key={index} className="text-center">
                            <div className={`w-full h-6 rounded flex items-center justify-center text-xs font-medium ${
                              day.minutes > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {day.minutes}m
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insights */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  📊 Week Insights
                </h3>
                <div className="space-y-2">
                  {insights.map((insight, index) => (
                    <div key={index} className="text-gray-700">
                      {insight}
                    </div>
                  ))}
                </div>
                
                {bestDay && (
                  <div className="mt-4 p-3 bg-white rounded-lg border">
                    <div className="text-sm font-medium text-gray-900">
                      🏆 Best Day: {bestDay.dayName} ({new Date(bestDay.date).toLocaleDateString()})
                    </div>
                    <div className="text-sm text-gray-600">
                      {bestDay.totalMinutes} minutes completed ({bestDay.completionPercentage.toFixed(0)}% of daily target)
                    </div>
                  </div>
                )}
              </div>

              {/* Week Summary */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Week Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Key Metrics</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Total time committed: {stats.totalTarget} minutes</li>
                      <li>• Total time completed: {stats.totalCompleted} minutes</li>
                      <li>• Days with activity: {stats.daysWithProgress}/7</li>
                      <li>• Average daily completion: {stats.averageDailyMinutes.toFixed(0)} minutes</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Performance Grade</h4>
                    <div className={`text-3xl font-bold ${
                      stats.overallCompletion >= 90 ? 'text-green-600' :
                      stats.overallCompletion >= 80 ? 'text-blue-600' :
                      stats.overallCompletion >= 70 ? 'text-yellow-600' :
                      stats.overallCompletion >= 60 ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {stats.overallCompletion >= 90 ? 'A+' :
                       stats.overallCompletion >= 80 ? 'A' :
                       stats.overallCompletion >= 70 ? 'B+' :
                       stats.overallCompletion >= 60 ? 'B' :
                       stats.overallCompletion >= 50 ? 'C+' :
                       stats.overallCompletion >= 40 ? 'C' : 'D'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {stats.overallCompletion >= 80 ? 'Excellent work!' :
                       stats.overallCompletion >= 60 ? 'Good effort!' :
                       'Keep improving!'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Close Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyReportModal; 